import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon
} from "@mui/icons-material";
import { useBrandRatingRubric } from "./hooks/useBrandRatingRubric";
import { useBrandRatingRubricSections } from "./hooks/useBrandRatingRubricSections";
import EditRubricForm from "./EditRubricForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import SectionForm from "./SectionForm";
import SubsectionForm from "./SubsectionForm";
import QuestionForm from "./QuestionForm";

export default function BrandRatingRubricPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rubric, loading, error, fetchRubric, updateRubric, deleteRubric, duplicateRubric } = useBrandRatingRubric();
  const sectionsApi = useBrandRatingRubricSections(id);

  // Main rubric editing state
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Section management state
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddSubsection, setShowAddSubsection] = useState(null);
  const [editingSubsection, setEditingSubsection] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // Flash animation state
  const [flashQuestionId, setFlashQuestionId] = useState(null);
  const [pendingNewQuestion, setPendingNewQuestion] = useState(null);

  React.useEffect(() => {
    if (id) {
      fetchRubric(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle flash animation for newly created questions
  React.useEffect(() => {
    if (pendingNewQuestion && rubric) {
      const targetSection = rubric.sections?.find(s => s.id === pendingNewQuestion.sectionId);
      const targetSubsection = targetSection?.subsections?.find(s => s.id === pendingNewQuestion.subsectionId);
      if (targetSubsection?.questions?.length > 0) {
        // Find the newest question (highest createdAt)
        const newestQuestion = targetSubsection.questions.reduce((newest, q) =>
          new Date(q.createdAt) > new Date(newest.createdAt) ? q : newest
        );
        setFlashQuestionId(newestQuestion.id);
        setTimeout(() => setFlashQuestionId(null), 1000);
        setPendingNewQuestion(null);
      }
    }
  }, [rubric, pendingNewQuestion]);

  if (loading) {
    return <Typography>Loading rubric…</Typography>;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {error?.message || error?.error || "Error loading rubric"}
        </Typography>
      </Box>
    );
  }

  if (!rubric) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Rubric not found.</Typography>
      </Box>
    );
  }

  // Close all edit forms
  const closeAllForms = () => {
    setShowAddSection(false);
    setEditingSection(null);
    setShowAddSubsection(null);
    setEditingSubsection(null);
    setShowAddQuestion(null);
    setEditingQuestion(null);
  };

  // Main rubric handlers
  const handleEdit = () => {
    closeAllForms();
    setIsEditing(true);
    setSuccessMessage("");
  };

  const handleSave = async (data) => {
    try {
      await updateRubric(id, data);
      await fetchRubric(id);
      setIsEditing(false);
      setSuccessMessage("Rubric updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
    setSuccessMessage("");
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteRubric(id);
      navigate("/brand-rating-rubrics");
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const handleDuplicate = async () => {
    try {
      await duplicateRubric(id);
      navigate("/brand-rating-rubrics");
      setSuccessMessage("Rubric duplicated successfully!");
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Section handlers
  const handleAddSection = () => {
    closeAllForms();
    sectionsApi.clearError();
    setShowAddSection(true);
  };

  const handleEditSection = (section) => {
    closeAllForms();
    sectionsApi.clearError();
    setEditingSection(section);
  };

  const handleSaveSection = async (data) => {
    try {
      if (editingSection) {
        await sectionsApi.updateSection(editingSection.id, data);
      } else {
        await sectionsApi.createSection(data);
      }
      await fetchRubric(id);
      closeAllForms();
      setSuccessMessage(`Section ${editingSection ? "updated" : "created"} successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleDeleteSection = (section) => {
    const questionCount = section.subsections?.reduce((total, sub) =>
      total + (sub.questions?.length || 0), 0) || 0;

    setDeleteConfirmation({
      type: "section",
      item: section,
      title: "Delete Section",
      message: `Are you sure you want to delete the section "${section.name}"?${
        questionCount > 0 ? ` This section contains ${questionCount} question${questionCount === 1 ? "" : "s"}.` : ""
      } This action cannot be undone.`
    });
  };

  // Subsection handlers
  const handleAddSubsection = (sectionId) => {
    closeAllForms();
    setShowAddSubsection(sectionId);
  };

  const handleEditSubsection = (sectionId, subsection) => {
    closeAllForms();
    setEditingSubsection({ sectionId, subsection });
  };

  const handleSaveSubsection = async (data) => {
    try {
      if (editingSubsection) {
        await sectionsApi.updateSubsection(
          editingSubsection.sectionId,
          editingSubsection.subsection.id,
          data
        );
      } else {
        await sectionsApi.createSubsection(showAddSubsection, data);
      }
      await fetchRubric(id);
      closeAllForms();
      setSuccessMessage(`Subsection ${editingSubsection ? "updated" : "created"} successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleDeleteSubsection = (sectionId, subsection) => {
    const questionCount = subsection.questions?.length || 0;

    setDeleteConfirmation({
      type: "subsection",
      item: { sectionId, subsection },
      title: "Delete Subsection",
      message: `Are you sure you want to delete the subsection "${subsection.name}"?${
        questionCount > 0 ? ` This subsection contains ${questionCount} question${questionCount === 1 ? "" : "s"}.` : ""
      } This action cannot be undone.`
    });
  };

  // Question handlers
  const handleAddQuestion = (sectionId, subsectionId) => {
    closeAllForms();
    setShowAddQuestion({ sectionId, subsectionId });
  };

  const handleEditQuestion = (sectionId, subsectionId, question) => {
    closeAllForms();
    setEditingQuestion({ sectionId, subsectionId, question });
  };

  const handleSaveQuestion = async (data) => {
    try {
      // Save current scroll position
      const scrollPosition = window.scrollY;

      if (editingQuestion) {
        await sectionsApi.updateQuestion(
          editingQuestion.sectionId,
          editingQuestion.subsectionId,
          editingQuestion.question.id,
          data
        );
      } else {
        await sectionsApi.createQuestion(
          showAddQuestion.sectionId,
          showAddQuestion.subsectionId,
          data
        );
      }
      await fetchRubric(id);

      // Store info for finding new question after refresh
      if (!editingQuestion && showAddQuestion) {
        setPendingNewQuestion({
          sectionId: showAddQuestion.sectionId,
          subsectionId: showAddQuestion.subsectionId
        });
      }

      // Trigger flash animation for edited questions immediately
      if (editingQuestion) {
        setFlashQuestionId(editingQuestion.question.id);
        setTimeout(() => setFlashQuestionId(null), 1000);
      }

      closeAllForms();
      setSuccessMessage(`Question ${editingQuestion ? "updated" : "created"} successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);

      // Restore scroll position after the DOM has updated
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleDeleteQuestion = (sectionId, subsectionId, question) => {
    setDeleteConfirmation({
      type: "question",
      item: { sectionId, subsectionId, question },
      title: "Delete Question",
      message: "Are you sure you want to delete this question? This action cannot be undone."
    });
  };

  // Generic delete confirmation handler
  const handleConfirmGenericDelete = async () => {
    try {
      const { type, item } = deleteConfirmation;

      if (type === "section") {
        await sectionsApi.deleteSection(item.id);
      } else if (type === "subsection") {
        await sectionsApi.deleteSubsection(item.sectionId, item.subsection.id);
      } else if (type === "question") {
        await sectionsApi.deleteQuestion(item.sectionId, item.subsectionId, item.question.id);
      }

      await fetchRubric(id);
      setDeleteConfirmation(null);
      setSuccessMessage(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        "@keyframes flash": {
          "0%": { backgroundColor: "success.light" },
          "50%": { backgroundColor: "success.main" },
          "100%": { backgroundColor: "transparent" }
        }
      }}
    >
      {/* Header with title and action buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Rubric: {rubric.category ?? `Rubric #${rubric.id}`}
        </Typography>

        {!isEditing && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={loading || sectionsApi.loading}
            >
              Edit Rubric Name
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleDuplicate}
              disabled={loading || sectionsApi.loading}
            >
              Duplicate Rubric
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={loading || sectionsApi.loading}
            >
              Delete Rubric
            </Button>
          </Box>
        )}
      </Box>

      {/* Success message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Edit form */}
      {isEditing && (
        <EditRubricForm
          rubric={rubric}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          loading={loading}
          error={error}
        />
      )}

      {/* Add Section Button */}
      {!isEditing && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSection}
            disabled={sectionsApi.loading}
          >
            Add Section
          </Button>
        </Box>
      )}

      {/* Add Section Form */}
      {showAddSection && (
        <SectionForm
          onSave={handleSaveSection}
          onCancel={() => setShowAddSection(false)}
          loading={sectionsApi.loading}
          error={sectionsApi.error}
          onClearError={sectionsApi.clearError}
        />
      )}

      {/* Edit Section Form */}
      {editingSection && (
        <SectionForm
          section={editingSection}
          onSave={handleSaveSection}
          onCancel={() => setEditingSection(null)}
          loading={sectionsApi.loading}
          error={sectionsApi.error}
          onClearError={sectionsApi.clearError}
        />
      )}

      {/* Rubric content */}
      {!isEditing && (
        <Box sx={{ mt: 3 }}>
          {rubric.sections?.length > 0
            ? (
              rubric.sections
                .slice()
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .map((section) => (
                  <Paper key={section.id} sx={{ mb: 3, p: 2 }}>
                    {/* Section Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" sx={{ borderLeft: 4, borderColor: "primary.main", pl: 2 }}>
                        {section.name}
                        {section.rubricScoreWeight != null && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        (weight {section.rubricScoreWeight})
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddSubsection(section.id)}
                          disabled={sectionsApi.loading}
                        >
                        Add Subsection
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSection(section)}
                          disabled={sectionsApi.loading}
                          aria-label="Edit section"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSection(section)}
                          disabled={sectionsApi.loading}
                          aria-label="Delete section"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Add Subsection Form */}
                    {showAddSubsection === section.id && (
                      <SubsectionForm
                        onSave={handleSaveSubsection}
                        onCancel={() => setShowAddSubsection(null)}
                        loading={sectionsApi.loading}
                        error={sectionsApi.error}
                      />
                    )}

                    {/* Edit Subsection Form */}
                    {editingSubsection?.sectionId === section.id && (
                      <SubsectionForm
                        subsection={editingSubsection.subsection}
                        onSave={handleSaveSubsection}
                        onCancel={() => setEditingSubsection(null)}
                        loading={sectionsApi.loading}
                        error={sectionsApi.error}
                      />
                    )}

                    {/* Subsections */}
                    {section.subsections
                      ?.slice()
                      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                      .map((sub) => (
                        <Box key={sub.id} sx={{ ml: 2, mb: 2 }}>
                          {/* Subsection Header */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                              {sub.name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddQuestion(section.id, sub.id)}
                                disabled={sectionsApi.loading}
                              >
                            Add Question
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleEditSubsection(section.id, sub)}
                                disabled={sectionsApi.loading}
                                aria-label="Edit subsection"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSubsection(section.id, sub)}
                                disabled={sectionsApi.loading}
                                aria-label="Delete subsection"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Questions */}
                          {sub.questions?.length > 0 && (
                            <List dense>
                              {sub.questions
                                .slice()
                                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                .map((q, qIndex) => (
                                  <React.Fragment key={q.id}>
                                    <ListItem
                                      sx={{
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        ...(flashQuestionId === q.id && {
                                          backgroundColor: "success.light",
                                          transition: "background-color 0.2s ease-in-out",
                                          animation: "flash 1s ease-in-out"
                                        })
                                      }}
                                    >
                                      {/* Show edit form instead of question content when editing */}
                                      {editingQuestion?.sectionId === section.id &&
                                       editingQuestion?.subsectionId === sub.id &&
                                       editingQuestion?.question.id === q.id
                                        ? (
                                          <QuestionForm
                                            question={editingQuestion.question}
                                            onSave={handleSaveQuestion}
                                            onCancel={() => setEditingQuestion(null)}
                                            loading={sectionsApi.loading}
                                            error={sectionsApi.error}
                                          />
                                        )
                                        : (
                                          <>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
                                              <ListItemText primary={q.text} />
                                              <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleEditQuestion(section.id, sub.id, q)}
                                                  disabled={sectionsApi.loading}
                                                  aria-label="Edit question"
                                                >
                                                  <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleDeleteQuestion(section.id, sub.id, q)}
                                                  disabled={sectionsApi.loading}
                                                  aria-label="Delete question"
                                                >
                                                  <DeleteIcon />
                                                </IconButton>
                                              </Box>
                                            </Box>
                                            {q.answerChoices?.length > 0 && (
                                              <List dense sx={{ ml: 3, mt: 1, width: "100%" }}>
                                                {q.answerChoices
                                                  .slice()
                                                  .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
                                                  .map((a) => (
                                                    <ListItem key={a.id} sx={{ py: 0 }}>
                                                      <ListItemText
                                                        primary={`${a.text} (${a.points} points)`}
                                                        primaryTypographyProps={{ variant: "body2" }}
                                                      />
                                                    </ListItem>
                                                  ))}
                                              </List>
                                            )}
                                          </>
                                        )}
                                    </ListItem>
                                    {qIndex < sub.questions.length - 1 && <Divider />}
                                  </React.Fragment>
                                ))}
                            </List>
                          )}

                          {/* Add Question Form appears at the bottom of the subsection */}
                          {showAddQuestion?.sectionId === section.id && showAddQuestion?.subsectionId === sub.id && (
                            <Box sx={{ mt: 2 }}>
                              <QuestionForm
                                onSave={handleSaveQuestion}
                                onCancel={() => setShowAddQuestion(null)}
                                loading={sectionsApi.loading}
                                error={sectionsApi.error}
                              />
                            </Box>
                          )}

                          {/* Add Question button at the bottom of subsection for convenience */}
                          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddQuestion(section.id, sub.id)}
                              disabled={sectionsApi.loading}
                              variant="outlined"
                            >
                              Add Question
                            </Button>
                          </Box>
                        </Box>
                      ))}
                  </Paper>
                ))
            )
            : (
              <Typography color="text.secondary">
              No sections defined for this rubric.
              </Typography>
            )}
        </Box>
      )}

      {/* Delete confirmation dialog for main rubric */}
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={loading}
        rubricCategory={rubric.category ?? `Rubric #${rubric.id}`}
      />

      {/* Generic delete confirmation dialog */}
      <Dialog
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
      >
        <DialogTitle>{deleteConfirmation?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteConfirmation?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmation(null)}
            disabled={sectionsApi.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGenericDelete}
            color="error"
            variant="contained"
            disabled={sectionsApi.loading}
          >
            {sectionsApi.loading ? "Deleting..." : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action error */}
      {(error || sectionsApi.error) && !isEditing && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.message || sectionsApi.error?.message || "An error occurred"}
        </Alert>
      )}
    </Box>
  );
}
